import React, { FC, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { Check, ChevronLeft, SuitcaseFill } from '../../../assets/icons'
import { Member } from '../../../models/Member'
import { Profile } from '../../../models/Profile'
import { Workplace } from '../../../models/Workplace'
import { colors } from '../../../theme/palette'
import { buildRow } from '../../pages/MyProfile'
import { L, L_HTML } from '../../providers/Lokalise'
import { Button } from '../../ui/Button/Button'
import { Circle } from '../../ui/Card'
import { Row } from '../../ui/Card/CardColumn'
import { CircleIcon } from '../../ui/Circle/Circle'
import { GmapsLink } from '../../ui/GmapsLink/GmapsLink'
import { Typography } from '../../ui/Typography/Typography'

interface WorkPlaceProps {
    selectedWorkplace: Workplace
    setSelectedWorkplace: (workplace: Workplace) => void
    workplaces: Workplace[]
    profile: Profile | Member
    beforContent?: JSX.Element
    afterContent?: JSX.Element
    showHo?: boolean
}

const useStyles = createUseStyles({
    column: {
        backgroundColor: colors.white,
        width: '100%',
        borderRadius: '.5rem',
        height: 'fit-content',
        marginBottom: '20px',
    },
    titleContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '1rem',
    },
    rowContainer: {
        padding: '.75rem 1rem .75rem 1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',

        '& a': {
            display: 'inline-flex',
        },
    },
    hr: {
        opacity: '.4',
        margin: '0',
    },
    dropdownIcon: {
        transform: 'rotate(90deg)',
        position: 'absolute',
        right: '27px',
        top: '29px',
        transition: 'all .15s',
        '&.active': {
            transform: 'rotate(-90deg)',
        },
    },
    dropdownItem: {
        position: 'relative',
        display: 'flex',
        border: 'none',
        padding: '0 0 10px',
    },
    dropdownItems: {
        width: '100%',
        // display: 'flex',
        flexDirection: 'column',
        padding: '10px 20px 20px 22px',
        display: 'none',
        height: 0,
        transition: 'height .15s',
        '&.active': {
            display: 'flex',
            height: 'auto',
        },
    },
    check: {
        position: 'relative',
        marginLeft: '5px',
        top: '-1px',
    },
})

const WorkplaceDropdown: FC<WorkPlaceProps> = ({
    workplaces,
    selectedWorkplace,
    setSelectedWorkplace,
    profile,
    beforContent,
    afterContent,
    showHo,
}) => {
    const classes = useStyles()
    const [isOpen, setIsOpen] = useState(false)

    const getPositionName = (
        workPlace: Workplace,
        member: Profile | Member
    ): string => {
        const id = workPlace.postionNumber
        const group = member.positions.find((pos) => +pos.id === +id)
        return group.name || '-'
    }

    const getGroupName = (member: Profile | Member): string => {
        const id = member.primaryWorkPlace.postionNumber
        const group = member.positions.find((pos) => +pos.id === +id)
        return group.group.name || '-'
    }

    const getPositionInfoRows = (workplace: Workplace): Row[] => {
        return [
            buildRow(
                getPositionName(workplace, profile),
                L('GENERISK_FORM_STILLING_LABEL')
            ),
            buildRow(workplace?.hoursPerWeek, L('GENERISK_FORM_TIMETAL_LABEL')),
            buildRow(getGroupName(profile), L('GENERISK_FORM_FAGGRUPPE_LABEL')),
        ]
    }

    let customedGmapsUrl = ''
    if (selectedWorkplace?.address) {
        const {
            addressLine1,
            addressLine2,
            addressLine3,
            postDistrict,
            postNumber,
        } = selectedWorkplace?.address
        customedGmapsUrl = `https://www.google.com/maps/search/?api=1&query=${addressLine1}+${addressLine2}+${addressLine3}%2C+${postNumber}+${postDistrict}`
    }

    return (
        <div className={classes.column}>
            <Button
                type="action"
                style={{
                    width: '100%',
                    borderRadius: 0,
                    cursor: workplaces.length > 1 ? 'pointer' : 'auto',
                }}
                onClick={() => {
                    if (workplaces.length > 1) {
                        setIsOpen(!isOpen)
                    }
                }}
            >
                <div className={classes.titleContainer}>
                    <Circle>
                        <CircleIcon>
                            <SuitcaseFill />
                        </CircleIcon>
                    </Circle>

                    <Typography tag="h3" size="M" spacing=".2rem" weight="bold">
                        {L_HTML('GENERISK_FORM_ANSÆTTELSE_TITEL')}
                    </Typography>

                    {workplaces.length > 1 ? (
                        <div
                            className={
                                classes.dropdownIcon + (isOpen ? ' active' : '')
                            }
                        >
                            <ChevronLeft width="10px" />
                        </div>
                    ) : null}
                </div>
            </Button>

            {workplaces.length > 1 ? (
                <div
                    className={
                        classes.dropdownItems + (isOpen ? ' active' : '')
                    }
                >
                    {workplaces.map((w) => {
                        return (
                            <Button
                                type="action"
                                onClick={() => {
                                    setIsOpen(false)
                                    setSelectedWorkplace(w)
                                }}
                            >
                                <div className={classes.dropdownItem}>
                                    <Typography
                                        tag="p"
                                        size="S"
                                        // color={colors.green500}
                                        weight={
                                            selectedWorkplace.number ===
                                            w.number
                                                ? 'bold'
                                                : 'normal'
                                        }
                                    >
                                        {w.name}
                                    </Typography>
                                    {selectedWorkplace.number === w.number ? (
                                        <div className={classes.check}>
                                            <Check width="25px" />
                                        </div>
                                    ) : null}
                                </div>
                            </Button>
                        )
                    })}
                </div>
            ) : null}

            <hr className={classes.hr} />

            {beforContent ? (
                <div className={classes.rowContainer}>{beforContent}</div>
            ) : null}

            <>
                <div className={classes.rowContainer}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                        }}
                    >
                        <Typography
                            tag="h4"
                            size="XXS"
                            color=" rgba(0, 0, 0, 0.4)"
                        >
                            Arbejdssted
                        </Typography>
                        <Typography
                            tag="p"
                            size="XS"
                            style={{ marginBottom: '10px' }}
                        >
                            {selectedWorkplace.name}
                        </Typography>
                        <GmapsLink customedGmapsUrl={customedGmapsUrl} />
                    </div>
                </div>
                {getPositionInfoRows(selectedWorkplace).map((row) => {
                    return (
                        <>
                            <hr className={classes.hr} />
                            <div className={classes.rowContainer}>
                                <div>
                                    <Typography
                                        tag="h4"
                                        size="XXS"
                                        color=" rgba(0, 0, 0, 0.4)"
                                    >
                                        {row.label}
                                    </Typography>
                                    <Typography tag="p" size="XS">
                                        {row.value}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    )
                })}
            </>

            {afterContent ? (
                <div className={classes.rowContainer}>{afterContent}</div>
            ) : null}
        </div>
    )
}

export { WorkplaceDropdown }
